import { Field, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useCallback } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { PasswordInput } from '@hivebrite/design-system/Inputs';

import shouldDisplayError from 'hb-react/shared/components/formik/utils/shouldDisplayError';

import { validatePassword } from './validate';

const Password = ({
  autoComplete = 'off',
  ariaLabel,
  ariaDescribedBy,
  disabled = false,
  id,
  label,
  labelMultiline = true,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  tooltip,
  validate = {},
  wrapperProps = {},
  hideError = false,
}) => {
  const formik = useFormikContext();
  const htmlId = id || name;

  const required = validate?.required;
  const maxLength = validate?.maxLength;
  const minLength = validate?.minLength;

  const handleValidate = useCallback(
    (value) =>
      validatePassword({
        value,
        required,
        maxLength,
        minLength,
      }),
    [required, minLength, maxLength],
  );
  const handleChange = useCallback(
    (e) => {
      formik.handleChange(e);
      if (onChange) {
        onChange(e.target.value);
      }
    },
    [formik, onChange],
  );
  const handleBlur = useCallback(
    (e) => {
      formik.handleBlur(e);
      if (onBlur) {
        onBlur();
      }
    },
    [formik, onBlur],
  );

  return (
    <Box mb={6} {...wrapperProps}>
      <Field name={name} validate={handleValidate}>
        {({
          field, // { name, value, onChange, onBlur }
          meta, // { error, touched, value }
          form,
        }) => (
          <PasswordInput
            {...field}
            autoComplete={autoComplete}
            ariaLabel={ariaLabel}
            ariaDescribedBy={ariaDescribedBy}
            clearable
            dataTestId="formik-password"
            disabled={disabled}
            error={shouldDisplayError({ meta, form })}
            id={htmlId}
            initialValue={meta.initialValue}
            label={label}
            labelMultiline={labelMultiline}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={onFocus}
            placeholder={placeholder}
            required={required}
            tooltip={tooltip}
            touched={meta.touched}
            hideError={hideError}
          />
        )}
      </Field>
    </Box>
  );
};

Password.propTypes = {
  name: PropTypes.string.isRequired,
  ariaDescribedBy: PropTypes.string,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  labelMultiline: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  validate: PropTypes.shape({
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    minLength: PropTypes.number,
  }),
  wrapperProps: PropTypes.object,
};

export default Password;
